import { Button, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { cacheService, userService } from "../../../../Core/Api/services";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import TableCellDateTime from "../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { DbPlatformAlertInfos } from "../../../../Core/Api/Types/db-platform-alert-infos";
import { PlatformAlertSeverity } from "../../../../Core/Api/Types/platform-alert-severity";
import TableCellAlertSeverity from "../../../../Components/Table/TableCellAlertSeverity/table-cell-alert-severity";
import ModalAddAlert, { ModalAddAlertElementProps } from "../../../../Components/Modals/ModalAddAlert/modal-add-alert";
import moment from "moment";
import { immerable } from "immer";
import { useTranslation } from "react-i18next";
import { DeleteOutline, ErrorOutlined, ErrorOutlineOutlined } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsSystemAlerts() {
    const { t } = useTranslation();
    // Table filter
    const [textFilter, setTextFilter] = React.useState("");
    // Loading
    const [isLoading, setIsLoading] = React.useState(true);
    // severityFilter filter
    const [severityFilter, setSeverityFilter] = React.useState<string>("");
    const [activeFilter, setActiveFilter] = React.useState<string>("");
    const enqueueSnackbar = useCustomSnackbar();

    // Table rows
    const [rows, setRows] = React.useState<DbPlatformAlertInfos[]>([]);
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };
    const apiGrid = useGridApiRef();

    const onConfirm = (alert: DbPlatformAlertInfos, id: number) => {
        setRows([
            {
                [immerable]: true,
                creationDate: alert.creationDate,
                endDate: alert.endDate,
                id: id,
                isActive: moment().toDate() < alert.endDate! && moment().toDate() > alert.startDate! ? true : false,
                isNew: true,
                message: alert.message,
                severity: alert.severity,
                startDate: alert.startDate,
                subject: alert.subject,
                userId: alert.userId,
                userName: alert.userName,
                platformId: cacheService.GetUserInfo()?.platformId ?? null,
            },
            ...rows,
        ]);
    };

    const handleNewAlertClick = () => {
        if (!openModal) {
            return;
        }

        const onClose: ModalAddAlertElementProps["onClose"] = (data) => { };

        openModal<ModalAddAlertElementProps>(ModalAddAlert, { onClose, onConfirm });
    };

    const handleChangeSeveritySelect = (event: SelectChangeEvent) => {
        setSeverityFilter(event.target.value);
    };

    const handleChangeActiveSelect = (event: SelectChangeEvent) => {
        setActiveFilter(event.target.value);
    };

    const onClickDelete = (id: number) => {
        userService.DeleteAlert(id).then(function (response) {
            if (response.success) {
                enqueueSnackbar(t("alert:success.alertDeleted"), "success");
                setRows((prevState) => {
                    return prevState.filter((x) => x.id !== id);
                });
            }
        });
    };

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const cols: GridColDef[] = React.useMemo(
        () => [
            {
                headerName: t("table:headerName.author"),
                field: "userName",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: Row) => data.value,
            },
            {
                headerName: t("common:creationDateLowerCase"),
                field: "creationDate",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: Row) => <TableCellDateTime data={data} />,
            },
            {
                headerName: t("common:startingDate"),
                field: "startDate",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: Row) => <TableCellDateTime data={data} />,
            },
            {
                headerName: t("common:endingDate"),
                field: "endDate",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: Row) => <TableCellDateTime data={data} />,
            },
            {
                headerName: t("table:headerName.isActive"),
                field: "isActive",
                type: "boolean",
                flex: CellSize.xxsmall,
                minWidth: CellSize.xxsmall,
                renderCell: (data: Row) => <TableCellBool value={data.value} />,
            },
            {
                headerName: t("common:severity"),
                field: "severity",
                type: "singleSelect",
                valueOptions: () => Object.keys(PlatformAlertSeverity),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: Row) => <TableCellAlertSeverity data={data} />,
            },
            {
                headerName: t("field:label.subject"),
                field: "subject",
                flex: CellSize.large,
                minWidth: CellSize.large,
                renderCell: (data: Row) => data.value,
            },
            {
                headerName: t("common:message"),
                field: "message",
                flex: CellSize.xxlarge,
                minWidth: CellSize.xxlarge,
                renderCell: (data: Row) => <Typography sx={{ my: 1.5 }}>{data.value}</Typography>,
            },
            {
                headerName: "",
                field: "actions",
                type: "actions",
                align: "right",
                getActions: (params: GridRowParams) => {
                    return [
                        <GridActionsCellItem
                            icon={<DeleteOutline color="error" />}
                            label={t("common:delete")}
                            onClick={() => onClickDelete(params.row["id"])}
                        />,
                    ];
                },
            },
        ],
        []
    );
    // #endregion

    React.useEffect(() => {
        setIsLoading(true);
        userService.GetAllAlertsList().then(function (response) {
            if (response.success) setRows(response.alerts ?? []);
            setIsLoading(false);
        });
    }, []);

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewAlertClick}
            disabled={!UserHasAcl(AclTypes.aclBackofficeUserCreate)}
        >
            {t("modal:title.newAlert")}
        </Button>
    );

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<ErrorOutlineOutlined />}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={severityFilter}
                onChange={handleChangeSeveritySelect}
            >
                <MenuItem value="" selected>
                    {t("menu:item.allSeverities")}
                </MenuItem>
                {Object.keys(PlatformAlertSeverity).map((severity) => (
                    <MenuItem value={severity} key={severity}>
                        {severity}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={activeFilter}
                onChange={handleChangeActiveSelect}
            >
                <MenuItem value="" selected>
                    {t("table:headerName.isActive")}
                </MenuItem>
                <MenuItem value="Yes">{t("common:active")}</MenuItem>
                <MenuItem value="No">{t("common:inactive")}</MenuItem>
            </Select>
        </>
    );

    return (
        <DataTable
            loading={isLoading}
            tableKey={TableUuids.SETTINGS_SYSTEM_ALERTS}
            columns={cols}
            rows={rows.filter(
                (row) =>
                    (severityFilter === "" || row.severity === severityFilter) &&
                    (activeFilter === "" || (activeFilter === "Yes" ? row.isActive : !row.isActive))
            )}
            additionalActions={additionalActions}
            additionalFilters={additionalFilters}
            rowHeight={64}
            getRowHeight={() => "auto"}
            pagination={true}
            initialState={{
                sorting: {
                    sortModel: [{ field: "creationDate", sort: "desc" }],
                },
            }}
            disableRowSelectionOnClick
            apiRef={apiGrid}
        />
    );
}

export default AdminSettingsSystemAlerts;
